import { Controller } from 'stimulus'
import formatCurrency from 'helpers/new-format-currency'

/**
 * Manages js for the ob results step (StimulusJS controller)
 *
 * @class LoanOptionsController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'button',
    'cards',
    'chosenLoanOption',
    'spinnerLand',
    'template',
    'title',
  ]

  /**
   * @function Controllers.LoanOptionsController.connect
   * @memberof Controllers
   * @description When the DOM goes live, a call goes out the the backend to
   * fetch loan option packages based on application attributes. These results
   * are then displayed to the user
   *
   * @return {undefined} manipulates DOM
   *
   */
  connect() {
    const type = this.data.get('applicationType')
    const slug = this.data.get('applicationSlug')
    const env  = this.data.get('env')

    if (env === 'test') {
      const fakeJson = [
        {
          'APR':              5.125,
          'AmortizationTerm': '30',
          'AmortizationType': 'Fixed',
          'BaseLoanAmount':   123000,
          'LoanTerm':         '30 Yr',
          'LoanType':         'Conforming',
          'Rate':             5.125,
          'TotalLoanAmount':  123000,
          'TotalPayment':     1300.77,
        }
      ]

      this._successResolution(fakeJson)
      return
    }

    fetch(`/v1/ob_results?app_slug=${slug}&app_type=${type}`, {
      method:      'GET',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
    })
    .then(response => {
      if (response.ok) {
        return response.json()
      }
      throw Error(`Request rejected with status ${response.status}`)
    })
    .then(json => { this._successResolution(json) })
    .catch(error => {
      this.element.dispatchEvent(new CustomEvent('full-app:next'))
    })
  }

  /**
   * @function Controllers.LoanOptionsController.chooseOption
   * @memberof Controllers
   * @description Kicks off method calls to set hidden form fields based on the
   * user's selection. After the hidden fields are set, shouts into the void,
   * 'full-app:next' to progress the form.
   *
   * @param {event} event - User initiated loan option selection
   *
   * @return {undefined} manipulates DOM
   *
   */
  chooseOption(event) {
    event.preventDefault()

    const option = parseInt(event.target.dataset.id)

    this.chosenLoanOptionTarget.value = JSON.stringify(this.loanOptions[option])

    event.target.dispatchEvent(new CustomEvent('full-app:next'))
  }


  // Private


  /**
   * @function Controllers.LoanOptionsController._successResolution
   * @memberof Controllers
   * @description DOM manipulations run on successful retrieval of options from
   * the backend. A card is created for each option that exists and these
   * become visible.
   * @private
   *
   * @param {Array.Object} options - The loan options to display in the UI.
   *
   * @return {undefined} manipulates DOM
   *
   */
  _successResolution(options) {
    this._disappearLoadingState()
    this._showSuccessTitle()

    this.loanOptions = options

    options.forEach((option, i) => {
      this._makeCard(option, i)
    })
  }

  /**
   * @function Controllers.LoanOptionsController._makeCard
   * @memberof Controllers
   * @description Function that takes a dom node representing a template (for
   * the styling), and returns a function that takes two parameters: option and
   * index. From the second round of parameters a template gets filled in and
   * finally appended to the card list.
   * @private
   *
   * @param {Object} option - object representation of a loan option
   * @param {Integer} index - object representation of a loan option
   *
   * @return {undefined} Manipulates DOM
   *
   */
  _makeCard(option, index) {
    const clone = this.templateTarget.cloneNode(true)

    if (index == 0) { clone.classList.add('loan-options__card--best') }

    clone.removeAttribute('style')
    clone.querySelector('.loan-options__card-title').textContent                  = this._createTitle(option)
    clone.querySelector('div[data-target="loan-option.loanAmount"]').textContent  = formatCurrency(option.TotalLoanAmount)
    clone.querySelector('div[data-target="loan-option.rate"]').textContent        = option.Rate + '%'
    clone.querySelector('div[data-target="loan-option.apr"]').textContent         = option.APR + '%'
    clone.querySelector('div[data-target="loan-option.payment"]').textContent     = formatCurrency(option.TotalPayment)
    clone.querySelector('div[data-target="loan-option.closingCost"]').textContent = formatCurrency(option.TotalLoanCost)
    clone.querySelector('a[data-target="loan-option.button"]').dataset.id         = index

    this.cardsTarget.append(clone)
  }

  /**
   * @function Controllers.LoanOptionsController._showSuccessTitle
   * @memberof Controllers
   * @description Shows the success title.
   * @private
   *
   * @return {undefined} Manipulates DOM
   *
   */
  _showSuccessTitle() {
    this.titleTarget.classList.remove('loan-options--hidden')
  }

  /**
   * @function Controllers.LoanOptionsController._disappearLoadingState
   * @memberof Controllers
   * @description Removes the spinner
   * @private
   *
   * @return {undefined} Manipulates DOM
   *
   */
  _disappearLoadingState() {
    this.spinnerLandTarget.remove()
  }

  /**
   * @function Controllers.LoanOptionsController._createTitle
   * @memberof Controllers
   * @description Stitches together a title from disparate data points for
   * display on the results card
   * @private
   *
   * @param {Object} option - object representation of a loan option
   *
   * @return {String} Title for card
   *
   */
  _createTitle(option) {
    const loanType         = this._loanTypeTranslation(option.LoanType)
    const loanTerm         = option.LoanTerm.split(' ').join('-')
    const amortizationType = option.AmortizationType
    const fixedTerm        = option.FixedTerm

    if (amortizationType.toLowerCase() === 'arm') {
      return `${loanType} ${fixedTerm} ${amortizationType}`
    } else {
      return `${loanTerm} ${amortizationType} ${loanType}`
    }
  }

  /**
   * @function Controllers.LoanOptionsController._loanTypeTranslation
   * @memberof Controllers
   * @description Translates 'Conforming' to 'Conventional', or passes loanType
   * through
   * @private
   *
   * @param {String} loanType - the loan type of a given option, i.e. 'FHA' or
   * 'Conventional'
   *
   * @return {String} Translated loan type
   *
   */
  _loanTypeTranslation(loanType) {
    if (loanType.toLowerCase() === 'conforming') {
      return 'Conventional'
    } else {
      return loanType
    }
  }
}
